// Estado y acciones del modal de escaneo/conteo de líneas en aprobación de refunds.
// La cámara vive en useRefundScanCamera; el match de códigos en refundScanModalUtils.
import { useCallback, useEffect, useRef, useState } from 'react'

import { toSafeNumber } from '../../../helpers/utils'
import { REFUND_APPROVAL_TEXT } from '../../constants/refundApprovalText'
import {
  getRefundLineOriginalQuantity,
  getRefundLineStatusUI
} from '../../constants/refundLineStatus'
import {
  findRefundLinesByCode,
  getLineDefaultQuantity,
  getModalPaperSize,
  getPrimaryBarcode,
  getQuantityInputRegex,
  isProcessedRescanLine,
  lineAllowsDecimals,
  parseVerifiedQuantity
} from '../helpers/refundScanModalUtils'
import { playScanBeep } from '../helpers/refundScanAudio'
import useRefundScanCamera from './useRefundScanCamera'

export default function useRefundScanModal({
  open,
  onClose,
  lines = [],
  onSave,
  initialLineUid = ''
}) {
  // --- Selección de línea / cantidad verificada ---
  const [search, setSearch] = useState('')
  const [selectedUid, setSelectedUid] = useState('')
  const [verifiedQuantity, setVerifiedQuantity] = useState('')
  const [manualError, setManualError] = useState('')
  const [saving, setSaving] = useState(false)
  const [processedRescanUid, setProcessedRescanUid] = useState('')
  const [packagingChoices, setPackagingChoices] = useState([])
  const initializedLineUidRef = useRef(null)

  const selectedLine = lines.find((line) => String(line.refund_item_uid) === String(selectedUid))

  const findLinesByCode = useCallback(
    (rawValue, options) => findRefundLinesByCode(lines, rawValue, options),
    [lines]
  )

  const selectLine = useCallback((line, searchValue = '', options = {}) => {
    if (!line) return

    setSelectedUid(line.refund_item_uid)
    setVerifiedQuantity(getLineDefaultQuantity(line))
    setSearch(searchValue || getPrimaryBarcode(line.product_barcode) || line.product_db_ref || '')
    setManualError('')
    setPackagingChoices([])
    setProcessedRescanUid(options.processedRescan ? String(line.refund_item_uid) : '')
  }, [])

  const resetState = useCallback(() => {
    setSearch('')
    setSelectedUid('')
    setVerifiedQuantity('')
    setManualError('')
    setProcessedRescanUid('')
    setPackagingChoices([])
  }, [])

  const applyMatchedLines = useCallback(
    (matchedLines, searchedCode) => {
      if (matchedLines.length === 1) {
        const line = matchedLines[0]
        selectLine(line, searchedCode, { processedRescan: isProcessedRescanLine(line) })
        return true
      }

      if (matchedLines.length > 1) {
        setSelectedUid('')
        setVerifiedQuantity('')
        setProcessedRescanUid('')
        setManualError('')
        setPackagingChoices(matchedLines)
        return true
      }

      return false
    },
    [selectLine]
  )

  // Código leído por cámara: matchea línea o muestra error; true = detener cámara.
  const handleDetectedCode = useCallback(
    (code) => {
      const scannedCode = String(code || '').trim()
      if (!scannedCode) return false

      setSearch(scannedCode)
      const matchedLines = findLinesByCode(scannedCode, { allowPartial: false })

      if (applyMatchedLines(matchedLines, scannedCode)) {
        playScanBeep()
        return true
      }

      setSelectedUid('')
      setVerifiedQuantity('')
      setProcessedRescanUid('')
      setPackagingChoices([])
      setManualError(`Código leído: ${scannedCode}. No coincide con esta devolución.`)
      playScanBeep()
      return true
    },
    [applyMatchedLines, findLinesByCode]
  )

  const {
    cameraOpen,
    cameraStatus,
    cameraError,
    cameraCodeError,
    canSwitchCamera,
    videoRef,
    canvasRef,
    openCamera,
    refocusCamera,
    switchCamera,
    stopCamera
  } = useRefundScanCamera({ onDetectedCode: handleDetectedCode })

  const startNewScan = useCallback(() => {
    initializedLineUidRef.current = '__new_scan__'
    resetState()
    stopCamera()
  }, [resetState, stopCamera])

  // --- Búsqueda manual y cantidad ---
  const handleSearchLine = useCallback(() => {
    const matchedLines = findLinesByCode(search)

    if (applyMatchedLines(matchedLines, search)) return

    setSelectedUid('')
    setVerifiedQuantity('')
    setPackagingChoices([])
    setManualError(REFUND_APPROVAL_TEXT.noLineForCode)
  }, [applyMatchedLines, findLinesByCode, search])

  const handleSelectPackaging = useCallback(
    (line) => {
      selectLine(line, search, { processedRescan: isProcessedRescanLine(line) })
    },
    [search, selectLine]
  )

  const closePackagingChoice = useCallback(() => {
    setPackagingChoices([])
  }, [])

  const handleSearchChange = useCallback(
    (event) => {
      const nextSearch = event.target.value

      if (!nextSearch.trim()) {
        resetState()
        return
      }

      setSearch(nextSearch)
      setManualError('')
    },
    [resetState]
  )

  const handleVerifiedQuantityChange = useCallback(
    (event) => {
      const nextValue = String(event.target.value || '')
      if (!getQuantityInputRegex(selectedLine).test(nextValue)) return

      setVerifiedQuantity(nextValue)
    },
    [selectedLine]
  )

  const handleClose = useCallback(() => {
    stopCamera()
    onClose()
  }, [onClose, stopCamera])

  // Al abrir (o cambiar initialLineUid), precarga la línea o limpia para un escaneo nuevo.
  useEffect(() => {
    if (!open) {
      initializedLineUidRef.current = null
      return
    }

    const initializationKey = initialLineUid || '__new_scan__'
    if (initializedLineUidRef.current === initializationKey) return
    initializedLineUidRef.current = initializationKey

    stopCamera()

    if (!initialLineUid) {
      resetState()
      return
    }

    const line = lines.find((item) => String(item.refund_item_uid) === String(initialLineUid))
    if (line) {
      selectLine(line, getPrimaryBarcode(line.product_barcode) || line.product_db_ref || '')
    }
  }, [initialLineUid, lines, open, resetState, selectLine, stopCamera])

  // --- Flags de UI derivados ---
  const hasSearch = search.trim().length > 0
  const isEditMode = Boolean(initialLineUid)
  const selectedLineStatus = selectedLine ? getRefundLineStatusUI(selectedLine) : null
  const selectedOriginalQuantity = selectedLine ? getRefundLineOriginalQuantity(selectedLine) : null
  const selectedUnit = selectedLine?.price_unit || selectedLine?.unit_of_sale || 'UN'
  const selectedVerifiedQuantity = selectedLine?.verified_quantity
  const allowsDecimals = lineAllowsDecimals(selectedLine)
  const hasVerifiedQuantity = String(verifiedQuantity).trim() !== ''
  const verifiedQuantityNumber = hasVerifiedQuantity
    ? parseVerifiedQuantity(verifiedQuantity, allowsDecimals)
    : Number.NaN
  const originalQuantityNumber = toSafeNumber(selectedOriginalQuantity)
  const quantityExceedsOriginal =
    Boolean(selectedLine) &&
    hasVerifiedQuantity &&
    Number.isFinite(verifiedQuantityNumber) &&
    verifiedQuantityNumber > originalQuantityNumber
  const quantityIsValid =
    Boolean(selectedLine) &&
    hasVerifiedQuantity &&
    Number.isFinite(verifiedQuantityNumber) &&
    (allowsDecimals || Number.isInteger(verifiedQuantityNumber)) &&
    verifiedQuantityNumber >= 0 &&
    verifiedQuantityNumber <= originalQuantityNumber
  const quantityError = quantityExceedsOriginal ? REFUND_APPROVAL_TEXT.quantityExceedsOriginal : ''
  const isUpdatingLine = selectedVerifiedQuantity != null
  const quantityHasChanged =
    !isUpdatingLine || verifiedQuantityNumber !== toSafeNumber(selectedVerifiedQuantity)
  const canSave = Boolean(selectedUid) && quantityIsValid && quantityHasChanged && !saving
  const hideScanControls =
    Boolean(selectedLine) &&
    !isEditMode &&
    processedRescanUid === String(selectedLine.refund_item_uid)
  const showManualSearchControls = !hideScanControls
  const showCameraControls = showManualSearchControls && !isEditMode
  const showSaveLoading = saving
  const processedNoticeLabel = REFUND_APPROVAL_TEXT.productAlreadyVerified
  const saveLoadingText =
    isEditMode || hideScanControls
      ? REFUND_APPROVAL_TEXT.updatingLine
      : REFUND_APPROVAL_TEXT.savingLine

  const saveButtonLabel = saving
    ? REFUND_APPROVAL_TEXT.updating
    : hideScanControls
      ? REFUND_APPROVAL_TEXT.editProcessedLine
      : isEditMode
        ? REFUND_APPROVAL_TEXT.updateLine
        : REFUND_APPROVAL_TEXT.saveAndContinue

  const modalPaperSize = getModalPaperSize({
    isEditMode,
    isUpdatingLine,
    hasSelectedLine: Boolean(selectedLine)
  })

  const handleSave = useCallback(async () => {
    if (!canSave) return

    setSaving(true)
    try {
      await onSave([{ refund_item_uid: selectedUid, verified_quantity: verifiedQuantityNumber }])
      if (isEditMode) {
        handleClose()
      } else {
        startNewScan()
      }
    } finally {
      setSaving(false)
    }
  }, [canSave, handleClose, isEditMode, onSave, selectedUid, startNewScan, verifiedQuantityNumber])

  return {
    search,
    verifiedQuantity,
    manualError,
    cameraOpen,
    cameraStatus,
    cameraError,
    cameraCodeError,
    canSwitchCamera,
    videoRef,
    canvasRef,
    selectedLine,
    selectedLineStatus,
    selectedOriginalQuantity,
    selectedUnit,
    selectedVerifiedQuantity,
    allowsDecimals,
    hasSearch,
    isEditMode,
    quantityIsValid,
    quantityError,
    isUpdatingLine,
    canSave,
    hideScanControls,
    showManualSearchControls,
    showCameraControls,
    showSaveLoading,
    processedNoticeLabel,
    saveLoadingText,
    saveButtonLabel,
    modalPaperSize,
    openCamera,
    refocusCamera,
    switchCamera,
    stopCamera,
    startNewScan,
    packagingChoices,
    handleSelectPackaging,
    closePackagingChoice,
    handleSearchLine,
    handleSearchChange,
    handleVerifiedQuantityChange,
    handleClose,
    handleSave
  }
}
